import { useRef, useState, type KeyboardEvent } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../primitives/Icon';

export interface ChatInputProps {
  onSend: (message: string) => void | false;
  placeholder?: string;
  /** Disabled state — closes the input and disables the send button. */
  disabled?: boolean;
  /** Optional handler for the leading attachment button. When omitted, the button still renders for visual consistency but is non-interactive. */
  onAttach?: () => void;
  onActivityChange?: (active: boolean) => void;
  className?: string;
}

/**
 * Chat composer bar. Enter sends; Shift+Enter inserts a newline (no
 * multi-line growth in this v1 — composer is single-line per JSX
 * prototype). The send button is disabled when the trimmed value is
 * empty.
 */
export function ChatInput({
  onSend,
  placeholder = 'Type a message…',
  disabled = false,
  onAttach,
  onActivityChange,
  className,
}: ChatInputProps) {
  const [value, setValue] = useState('');
  const focused = useRef(false);

  const send = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    if (onSend(trimmed) === false) return;
    setValue('');
    onActivityChange?.(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div
      className={cn(
        'flex w-full items-center gap-2 rounded-md border border-border bg-bg-surface px-2 py-2',
        'focus-within:border-accent-500 focus-within:ring-1 focus-within:ring-accent-500',
        disabled && 'pointer-events-none opacity-60',
        className,
      )}
    >
      <button
        type="button"
        aria-label="Add attachment"
        onClick={onAttach}
        disabled={!onAttach || disabled}
        className={cn(
          'inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-text-secondary',
          'hover:bg-bg-elevated hover:text-text-primary',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500',
          'transition-colors',
          'disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-text-secondary',
        )}
      >
        <Icon name="plusCircle" size={16} />
      </button>
      <input
        type="text"
        value={value}
        onChange={(e) => { setValue(e.target.value); onActivityChange?.(focused.current && e.target.value.trim().length > 0); }}
        onFocus={(e) => { focused.current = true; onActivityChange?.(e.currentTarget.value.trim().length > 0); }}
        onBlur={() => { focused.current = false; onActivityChange?.(false); }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className="flex-1 border-0 bg-transparent text-body text-text-primary outline-none placeholder:text-text-muted"
      />
      <button
        type="button"
        aria-label="Send message"
        onClick={send}
        disabled={disabled || !value.trim()}
        className={cn(
          'inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md',
          'bg-accent-500 text-white hover:bg-accent-600 active:bg-accent-700',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-app',
          'transition-colors',
          'disabled:cursor-default disabled:bg-bg-elevated disabled:text-text-muted',
        )}
      >
        <Icon name="arrowRight" size={16} />
      </button>
    </div>
  );
}
